import React, { useRef, useState, useLayoutEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faQrcode, faUsers, faChurch, faHourglassHalf,
  faRedo, faVoteYea, faComments, faUserTie, faChair,
  faCircleCheck, faCircleXmark, faForward,
  faMobileScreenButton, faListCheck, faHandsPraying
} from '@fortawesome/free-solid-svg-icons';
import { QRCodeSVG } from 'qrcode.react';
import { Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import './TelaoView.css';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend, ChartDataLabels);

// Paleta de cores das fatias/barras (o eleito recebe verde à parte)
const PALETTE = ['#3b82f6', '#8b5cf6', '#f59e0b', '#0ea5e9', '#ec4899', '#14b8a6', '#f97316', '#64748b'];
const WINNER_COLOR = '#10b981';
const REJECTED_COLOR = '#475569';

function buildColors(results, conclusive, winnerName, rejectedNames = []) {
  return results.map((r, i) => {
    if (rejectedNames.includes(r.name)) return REJECTED_COLOR;
    // várias vagas usam a flag "elected"; vaga única usa o winnerName
    const isWinner = r.elected || (conclusive && r.name === winnerName);
    return isWinner ? WINNER_COLOR : PALETTE[i % PALETTE.length];
  });
}

// Etiqueta discreta de origem da indicação (iconOnly = só o ícone, para listas longas)
const OriginTag = ({ nominatedBy, iconOnly = false }) => (
  nominatedBy === 'comissao' ? (
    <span className="telao-origin origin-com" title="Indicado pela Comissão">
      <FontAwesomeIcon icon={faUserTie} />{!iconOnly && ' Comissão'}
    </span>
  ) : (
    <span className="telao-origin origin-igr" title="Indicado pela Igreja">
      <FontAwesomeIcon icon={faChurch} />{!iconOnly && ' Igreja'}
    </span>
  )
);

// Número fixo do candidato (posição no cadastro). Fallback: posição na lista.
const candNumber = (c, i) => c.number || i + 1;

// A partir de quantos nomes o telão entra no modo "lista longa"
const DENSE_FROM = 10;

/*
 * Calcula o melhor arranjo (colunas + tamanho de fonte) para N itens
 * caberem inteiros numa caixa W x H, sem rolagem.
 * Modelo (em unidades "em" da fonte do item, que é o que o CSS usa):
 *   altura do item  = ITEM_H_EM  (linha 1.2 + padding vertical 1.1)
 *   espaço entre    = GAP_EM
 *   largura do item = extraEm + maxChars * CHAR_EM
 * Escolhe as colunas que dão a MAIOR fonte; empate → menos colunas.
 */
const ITEM_H_EM = 2.3;
const GAP_EM = 0.45;
const CHAR_EM = 0.56;
export function computeFit({ width, height, count, maxChars, extraEm, minFont, maxFont, maxCols = 6 }) {
  if (!width || !height || !count) return { cols: 1, font: maxFont };
  const itemWEm = extraEm + maxChars * CHAR_EM;
  let best = { cols: 1, font: 0 };
  for (let cols = 1; cols <= Math.min(count, maxCols); cols++) {
    const rows = Math.ceil(count / cols);
    const byH = height / (rows * ITEM_H_EM + (rows - 1) * GAP_EM);
    const byW = width / (cols * itemWEm + (cols - 1) * GAP_EM);
    const font = Math.min(byH, byW, maxFont);
    if (font > best.font + 0.25) best = { cols, font };
  }
  return { cols: best.cols, font: Math.max(minFont, Math.floor(best.font * 0.96)) };
}

/*
 * Grade que se ajusta sozinha ao espaço disponível (telão/projetor ou prévia).
 * O contêiner externo tem o tamanho definido pelo layout (flex), e a grade
 * interna é absoluta — assim medir o espaço não depende do conteúdo.
 */
const FitGrid = ({ count, maxChars, extraEm, compact, className = '', children }) => {
  const outerRef = useRef(null);
  const minFont = compact ? 9 : 14;
  const maxFont = compact ? 18 : 38;
  const [fit, setFit] = useState({ cols: 1, font: maxFont });

  useLayoutEffect(() => {
    const el = outerRef.current;
    if (!el) return undefined;
    const update = () => {
      const next = computeFit({
        width: el.clientWidth, height: el.clientHeight,
        count, maxChars, extraEm, minFont, maxFont
      });
      setFit(prev => (prev.cols === next.cols && prev.font === next.font ? prev : next));
    };
    update();
    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(update);
      ro.observe(el);
      return () => ro.disconnect();
    }
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [count, maxChars, extraEm, minFont, maxFont]);

  return (
    <div ref={outerRef} className="telao-fit">
      <div
        className={`telao-fit-grid ${className}`}
        style={{ gridTemplateColumns: `repeat(${fit.cols}, minmax(0, 1fr))`, fontSize: `${fit.font}px` }}
      >
        {children}
      </div>
    </div>
  );
};

// Maior nome da lista (limitado: nomes enormes ganham reticências, não encolhem todos)
const longestName = (items) => Math.min(28, Math.max(8, ...items.map(i => (i.name || '').length)));

/*
 * Componente visual do "telão".
 * Reutilizado tanto na prévia do painel da secretaria quanto na página
 * pública em tela cheia (/telao?code=XXXX). Recebe apenas dados via props.
 *
 * Prioridade de exibição:
 *  1) votação aberta
 *  2) resultado da última rodada do ministério em destaque
 *  3) ministério em destaque: discussão de nomes, eleito(s) ou pulado
 *  4) tela de espera (QR code)
 */
const TelaoView = ({ state, lastResult, qrUrl, compact = false }) => {
  const rootCls = `telao ${compact ? 'telao-compact' : ''}`;

  if (!state) {
    return (
      <div className={rootCls}>
        <div className="telao-center telao-muted">
          <FontAwesomeIcon icon={faHourglassHalf} className="telao-icon" />
          <p>Carregando…</p>
        </div>
      </div>
    );
  }

  const { title, publicCode, checkins, expectedVoters, voting, featured } = state;

  const qrSize = (small) => (compact ? (small ? 72 : 96) : (small ? 120 : 168));
  const QrBox = ({ label = 'Escaneie para votar', small = false }) => (
    <div className={`telao-qr ${small ? 'telao-qr-small' : ''}`}>
      {qrUrl && <QRCodeSVG value={qrUrl} size={qrSize(small)} level="M" includeMargin />}
      <div className="telao-qr-info">
        <span className="telao-qr-label"><FontAwesomeIcon icon={faQrcode} /> {label}</span>
        <span className="telao-code">{publicCode}</span>
      </div>
    </div>
  );

  const Presence = () => (
    <div className="telao-presence">
      <FontAwesomeIcon icon={faUsers} />
      <span className="telao-presence-num">{checkins || 0}{expectedVoters ? ` / ${expectedVoters}` : ''}</span>
      <span className="telao-presence-txt">presentes</span>
    </div>
  );

  // parentName: etapa derivada (ex.: "Ancionato · Primeiro Ancião")
  const Header = ({ name, parentName }) => (
    <div className="telao-header">
      <h1>
        <FontAwesomeIcon icon={faChurch} />{' '}
        {parentName && <span className="telao-parent-name">{parentName} ·</span>}{' '}
        {name}
      </h1>
      <Presence />
    </div>
  );

  // Resultado: fonte principal é o servidor (featured.lastRound), assim o telão
  // sobrevive a recarregamentos. lastResult (evento do socket) é só fallback
  // para quando o estado ainda não trouxe o bloco "featured".
  let result = null;
  if (!voting && featured && featured.view === 'result' && featured.lastRound) {
    result = {
      ...featured.lastRound,
      name: featured.name,
      parentName: featured.parentName || '',
      confirming: featured.status === 'confirming'
    };
  } else if (!voting && featured === undefined && lastResult) {
    result = lastResult;
  }

  // 1) Resultado (rodada encerrada)
  if (result) {
    const { name, results = [], totalVotes, conclusive, winnerName, multiSeat, voters, seatsOpen, confirming } = result;
    const nominees = result.nominated
      || (multiSeat ? results.filter(r => r.elected).map(r => ({ name: r.name, rejected: false })) : []);
    const rejectedNames = nominees.filter(n => n.rejected).map(n => n.name);
    const allColors = buildColors(results, conclusive, winnerName, rejectedNames);
    // Lista longa: o gráfico mostra só quem recebeu voto (no máx. CHART_MAX barras)
    // e a legenda vira grade com todos. results já vem ordenado por votos.
    const CHART_MAX = 12;
    const manyResults = results.length > 8;
    const chartResults = manyResults
      ? results.filter(r => r.votes > 0).slice(0, CHART_MAX)
      : results;
    const colors = allColors.slice(0, chartResults.length);
    const zeroCount = manyResults ? results.filter(r => r.votes === 0).length : 0;
    const legendResults = manyResults ? results.filter(r => r.votes > 0) : results;
    // até 4 candidatos → rosca; acima → barras horizontais (evita fatias minúsculas)
    const useDoughnut = chartResults.length <= 4;
    const majorityRule = state.rule ? state.rule === 'absolute_majority' : true;

    const doughnutData = {
      labels: chartResults.map(r => r.name),
      datasets: [{
        data: chartResults.map(r => r.votes),
        backgroundColor: colors,
        borderColor: '#0f2540',
        borderWidth: 3
      }]
    };
    const doughnutOptions = {
      responsive: true,
      maintainAspectRatio: false,
      cutout: '55%',
      plugins: {
        legend: { display: false },
        tooltip: { enabled: false },
        datalabels: {
          color: '#fff',
          textAlign: 'center',
          font: { weight: 'bold', size: compact ? 11 : 16 },
          formatter: (value, ctx) => {
            const r = chartResults[ctx.dataIndex];
            if (totalVotes > 0 && (value / totalVotes) < 0.05) return `${r.percent}%`;
            return compact ? `${r.percent}%` : `${r.name}\n${r.votes} · ${r.percent}%`;
          }
        }
      }
    };

    const barData = {
      labels: chartResults.map(r => r.name),
      datasets: [{
        data: chartResults.map(r => r.votes),
        backgroundColor: colors,
        borderRadius: 6
      }]
    };
    const barOptions = {
      indexAxis: 'y',
      responsive: true,
      maintainAspectRatio: false,
      layout: { padding: { right: compact ? 40 : 70 } },
      plugins: {
        legend: { display: false },
        tooltip: { enabled: false },
        datalabels: {
          anchor: 'end',
          align: 'end',
          color: '#e2e8f0',
          font: { weight: 'bold', size: compact ? 11 : 15 },
          formatter: (value, ctx) => `${value} · ${chartResults[ctx.dataIndex].percent}%`
        }
      },
      scales: {
        x: { beginAtZero: true, ticks: { precision: 0, color: '#94a3b8' }, grid: { color: 'rgba(255,255,255,0.08)' } },
        y: { ticks: { color: '#e2e8f0', font: { size: compact ? 11 : 14 } }, grid: { display: false } }
      }
    };

    return (
      <div className={rootCls}>
        <Header name={name} parentName={result.parentName} />
        {/* Legenda das cores no topo (com muitos nomes, a legenda de baixo não cabe inteira) */}
        {multiSeat && (
          <div className="telao-color-key">
            <span className="telao-key-item">
              <i className="telao-key-dot" style={{ background: WINNER_COLOR }} />
              <strong>Verde</strong> = indicados (os mais votados, para {seatsOpen || 1} {(seatsOpen || 1) === 1 ? 'vaga' : 'vagas'})
            </span>
            <span className="telao-key-item">
              <i className="telao-key-dot telao-key-multi" />
              Outras cores = demais candidatos
            </span>
            {rejectedNames.length > 0 && (
              <span className="telao-key-item">
                <i className="telao-key-dot" style={{ background: REJECTED_COLOR }} />
                <s>Cinza riscado</s> = não aceitou / rejeitado
              </span>
            )}
            <span className="telao-key-item telao-key-note">Empate na linha de corte deixa a vaga em aberto</span>
          </div>
        )}
        {multiSeat ? (
          nominees.length > 0 ? (
            <div className="telao-winner telao-nominees-banner">
              <span className="telao-nominees-title">
                <FontAwesomeIcon icon={faCircleCheck} /> {nominees.length > 1 ? 'Indicados' : 'Indicado'}:
              </span>
              {nominees.map(n => (
                <span
                  key={n.name}
                  className={`telao-nominee ${n.rejected ? 'is-rejected' : ''}`}
                  title={n.rejected ? 'Não aceito — a vaga será reaberta' : 'Indicado pela votação'}
                >
                  <FontAwesomeIcon icon={n.rejected ? faCircleXmark : faCircleCheck} /> {n.name}
                </span>
              ))}
            </div>
          ) : (
            <div className="telao-inconclusive">
              <FontAwesomeIcon icon={faRedo} /> Sem indicados — nova rodada
            </div>
          )
        ) : conclusive ? (
          <div className="telao-winner">
            <FontAwesomeIcon icon={faCircleCheck} /> Eleito: <strong>{winnerName}</strong>
          </div>
        ) : (
          <div className="telao-inconclusive">
            <FontAwesomeIcon icon={faRedo} /> Resultado inconclusivo — nova rodada
          </div>
        )}

        <div className="telao-result-body">
          <div className={`telao-chart ${useDoughnut ? 'is-doughnut' : 'is-bar'}`}>
            {useDoughnut ? (
              <>
                <Doughnut data={doughnutData} options={doughnutOptions} />
                <div className="telao-donut-center">
                  <span className="telao-donut-num">{totalVotes}</span>
                  <span className="telao-donut-txt">votos</span>
                </div>
              </>
            ) : (
              <Bar data={barData} options={barOptions} />
            )}
          </div>

          <div className={`telao-legend ${manyResults ? 'is-many' : ''}`}>
            <div className="telao-legend-rows">
              {legendResults.map((r, i) => {
                const isRejected = rejectedNames.includes(r.name);
                const isWinner = !isRejected && (r.elected || (conclusive && r.name === winnerName));
                return (
                  <div key={r.name} className={`telao-legend-row ${isWinner ? 'is-winner' : ''} ${isRejected ? 'is-rejected' : ''}`}>
                    <span className="telao-legend-dot" style={{ background: allColors[i] }} />
                    <span className="telao-legend-name" title={r.name}>{r.name}</span>
                    <span className="telao-legend-val">{r.votes} · {r.percent}%</span>
                  </div>
                );
              })}
            </div>
            {zeroCount > 0 && (
              <div className="telao-legend-rule">
                + {zeroCount} {zeroCount === 1 ? 'nome sem votos' : 'nomes sem votos'}
              </div>
            )}
            {majorityRule && !multiSeat && (
              <div className="telao-legend-rule">
                <span className="telao-rule-line" /> Maioria absoluta: mais de 50%
              </div>
            )}
            {multiSeat && (
              <div className="telao-legend-rule">
                <span className="telao-rule-line" /> {seatsOpen} vaga(s) · % sobre {voters || 0} votante(s)
              </div>
            )}
            <div className="telao-legend-total">
              {multiSeat ? `${voters || 0} votante(s) · ${totalVotes} marcações` : `Total de votos: ${totalVotes}`}
            </div>
          </div>
        </div>
        {confirming && (
          <div className="telao-hint">
            <FontAwesomeIcon icon={faListCheck} /> Aguardando a confirmação dos indicados pela secretaria.
          </div>
        )}
      </div>
    );
  }

  // 2) Votação em andamento
  if (voting) {
    const multiVote = voting.votesPerVoter > 1;
    const vCands = voting.candidates || [];
    const dense = vCands.length >= DENSE_FROM;
    return (
      <div className={`${rootCls} ${dense ? 'telao-dense' : ''}`}>
        <Header name={voting.name} parentName={voting.parentName} />
        <div className="telao-badges">
          <span className="telao-stage stage-live"><FontAwesomeIcon icon={faVoteYea} /> Votação aberta</span>
          {voting.round > 1 && <span className="telao-round-badge">Rodada {voting.round}</span>}
          {multiVote && (
            <span className="telao-round-badge">
              {voting.seatsOpen} vaga(s) · escolha até {voting.votesPerVoter} nomes
            </span>
          )}
        </div>

        <div className="telao-voting-body">
          <FitGrid count={vCands.length} maxChars={longestName(vCands)} extraEm={3.6} compact={compact}>
            {vCands.map((c, i) => (
              <div key={c._id} className="telao-fit-item">
                <span className="telao-num">{candNumber(c, i)}</span>
                <span className="telao-candidate-name" title={c.name}>{c.name}</span>
              </div>
            ))}
          </FitGrid>
          <div className="telao-side">
            <div className="telao-votes-count">
              <span className="telao-votes-num">{voting.votesCount}</span>
              <span className="telao-votes-txt">votos{expectedVoters ? ` de ${expectedVoters}` : ''}</span>
            </div>
            <QrBox small={dense} />
          </div>
        </div>
        <div className="telao-hint">
          <FontAwesomeIcon icon={faMobileScreenButton} />
          Escaneie o QR code, toque {multiVote ? `em até ${voting.votesPerVoter} nomes` : 'no nome escolhido'} e confirme.
        </div>
      </div>
    );
  }

  // 3) Ministério em destaque (sem votação aberta)
  if (featured) {
    const electedList = featured.electedList || [];

    // 3a) Eleito(s) definido(s)
    if (featured.view === 'elected' && electedList.length > 0) {
      const byAcclamation = featured.electedSource === 'manual';
      return (
        <div className={rootCls}>
          <Header name={featured.name} parentName={featured.parentName} />
          <div className="telao-elected">
            <FontAwesomeIcon icon={faHandsPraying} className="telao-elected-icon" />
            <span className="telao-elected-label">
              {electedList.length > 1 ? 'Eleitos para servir' : 'Eleito para servir'}
            </span>
            <div className="telao-elected-names">
              {electedList.map(e => (
                <div key={e.name} className="telao-elected-name">
                  {e.name}
                  {e.votes > 0 && <span className="telao-elected-votes">{e.votes} voto(s) · {e.percent}%</span>}
                </div>
              ))}
            </div>
            <span className="telao-elected-pray">
              {electedList.length > 1 ? 'Oremos por eles' : 'Oremos por este irmão'}
            </span>
            <span className="telao-elected-source">
              {byAcclamation ? 'por aclamação da assembleia' : 'por votação da assembleia'}
            </span>
          </div>
        </div>
      );
    }

    // 3b) Ministério sem votação neste ano
    if (featured.view === 'skipped') {
      return (
        <div className={rootCls}>
          <Header name={featured.name} parentName={featured.parentName} />
          <div className="telao-center telao-skipped">
            <FontAwesomeIcon icon={faForward} className="telao-icon" />
            <p>Sem votação para este ministério neste ano.</p>
          </div>
        </div>
      );
    }

    // 3c) Discussão de nomes (padrão)
    const candidates = featured.candidates || [];
    const seats = featured.seats || 1;
    const seatsLeft = featured.seatsLeft !== undefined ? featured.seatsLeft : seats;
    const partial = seats > 1 && electedList.length > 0;
    const dense = candidates.length >= DENSE_FROM;
    // Andamento da discussão (marcado pela secretaria; pode haver vários "agora")
    const talkingNow = candidates.map((c, i) => ({ ...c, n: candNumber(c, i) })).filter(c => c.discussion === 'now');
    const doneCount = candidates.filter(c => c.discussion === 'done').length;
    const tracking = talkingNow.length > 0 || doneCount > 0;
    return (
      <div className={`${rootCls} ${dense ? 'telao-dense' : ''}`}>
        <Header name={featured.name} parentName={featured.parentName} />
        <div className="telao-badges">
          <span className="telao-stage stage-names"><FontAwesomeIcon icon={faComments} /> Discussão de nomes</span>
          {tracking && (
            <span className="telao-disc-progress">
              <FontAwesomeIcon icon={faCircleCheck} /> {doneCount} de {candidates.length} já apresentados
            </span>
          )}
          {seats > 1 && (
            <span className="telao-round-badge">
              <FontAwesomeIcon icon={faChair} /> {partial ? `${seatsLeft} de ${seats} vagas em aberto` : `${seats} vagas`}
            </span>
          )}
        </div>

        {partial && (
          <div className="telao-already">
            <FontAwesomeIcon icon={faCircleCheck} /> Já eleitos: <strong>{electedList.map(e => e.name).join(', ')}</strong>
          </div>
        )}

        {talkingNow.length > 0 && (
          <div className="telao-disc-now">
            <FontAwesomeIcon icon={faComments} className="telao-disc-now-ico" />
            <span>Em discussão agora:</span>
            {talkingNow.map(c => (
              <strong key={c._id} className="telao-disc-now-name">
                <span className="telao-num">{c.n}</span> {c.name}
              </strong>
            ))}
          </div>
        )}

        <div className="telao-voting-body">
          {candidates.length === 0 ? (
            <div className="telao-names-empty">
              <FontAwesomeIcon icon={faComments} className="telao-icon" />
              <p>Os nomes aparecerão aqui conforme forem apresentados.</p>
            </div>
          ) : (
            <FitGrid
              count={candidates.length}
              maxChars={longestName(candidates)}
              extraEm={dense ? 5.5 : 9.5}
              compact={compact}
            >
              {candidates.map((c, i) => (
                <div key={c._id} className={`telao-fit-item telao-name-card disc-${c.discussion || 'none'}`}>
                  <span className="telao-num">{candNumber(c, i)}</span>
                  <span className="telao-candidate-name" title={c.name}>{c.name}</span>
                  {c.discussion === 'now' && <FontAwesomeIcon icon={faComments} className="telao-disc-ico" />}
                  {c.discussion === 'done' && <FontAwesomeIcon icon={faCircleCheck} className="telao-disc-ico" />}
                  <OriginTag nominatedBy={c.nominatedBy} iconOnly={dense} />
                </div>
              ))}
            </FitGrid>
          )}
          <div className="telao-side telao-side-names">
            <QrBox label="Registre sua presença" small />
          </div>
        </div>
        <div className="telao-hint">
          <FontAwesomeIcon icon={faHourglassHalf} /> A votação será aberta em seguida. Tenha o celular em mãos.
        </div>
      </div>
    );
  }

  // 4) Aguardando
  return (
    <div className={rootCls}>
      <div className="telao-header">
        <h1><FontAwesomeIcon icon={faVoteYea} /> {title}</h1>
        <Presence />
      </div>
      <div className="telao-waiting">
        <QrBox />
        <p className="telao-waiting-text">Escaneie o QR code ou acesse o link e digite o código <strong>{publicCode}</strong> para participar.</p>
      </div>
    </div>
  );
};

export default TelaoView;
